import React, { Fragment, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Button } from 'grommet';
import { CustomCheckbox } from '../../../../components';

import { postChangeStatus } from '../../../../api';
import { formatNumber } from '../../../../commons';
import { PageWrapper, Loading, Separator, ListLevel, Alert } from '../../../../components';

import { useForm, useMigrationSummary, useEnvironmentDetail } from '../../../../hooks';

import urlRoutes from '../../../../config/url-routes';

import { migrationStatus, messageDictionary } from '../../../../constants';

const FormApproveMigration = () => {
  const { goBack, push } = useHistory();
  const { migrationId } = useParams();

  const [acceptTerm, setAcceptTerm] = useState(false);

  const { loading, data } = useMigrationSummary(migrationId, true);
  const { loading: loadingEnv, data: dataEnv } = useEnvironmentDetail(migrationId);
  const { values, errors, successful, submitting, handleSubmit, initialize } = useForm();

  useEffect(() => {
    initialize({
      migrationId,
      status: migrationStatus.finalMigrationInProgress,
    });
  }, [initialize, migrationId]); // Adiciona as dependências `initialize` e `migrationId`  

  if(loading || loadingEnv) {
    return <Loading />;
  }

  if(successful || dataEnv.status !== migrationStatus.environmentReadyForAnalisys) {
    return (
      <PageWrapper>
        <div className='approve-success'>
          <p className='approve-success__message'>
            Falta pouco! Agora estamos enviando seus dados para o ambiente de produção, aguarde e seja bem vindo ao Kenlo Imob!
          </p>
        </div>

        <div className='approve-actions'>
          <Button
            color='dark-3'
            label='Ok'
            onClick={() =>
              push(urlRoutes.migrationsUrl)
            }
          />
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className='approve-header'>
        <h2 className='approve-header__title'>
          Deseja realmente APROVAR essa migração? Após aprovação iniciaremos o processo de envio dos dados para o ambiente de produção.
        </h2>
      </div>

      <div className='approve-summary'>
            <Fragment>
              <ListLevel
                title = 'Total de imóveis'
                value = {formatNumber(data.properties?.total)}
                subList = {[
                  {
                    label: 'Sem corretor (captador, indicação, promotor, fotografia)',
                    value: formatNumber(data.properties?.no_broker),
                  },
                  {
                    label: 'Sem tipo (casa, apartamento, etc.)',
                    value: formatNumber(data.properties?.no_type),
                  },
                  {
                    label: 'Sem preço (venda/locação)',
                    value: formatNumber(data.properties?.no_price),
                  },
                  {
                    label: 'Sem área (total, útil/construída, comum, privativa)',
                    value: formatNumber(data.properties?.no_area),
                  },
                  {
                    label: 'Sem complemento no endereço (imóveis verticais: apartamento, cobertura, kitnet, etc.)',
                    value: formatNumber(data.properties?.vertical_without_add_on_address),
                  },
                  {
                    label: 'Sem proprietário',
                    value: formatNumber(data.properties?.no_owners),
                  },
                  {
                    label: 'Sem endereço/CEP',
                    value: formatNumber(data.properties?.no_zipcode),
                    warning: true,
                    warningText: 'bla bla bla'
                  },
                  {
                    label: 'Sem empreendimento (imóveis verticais: apartamento, cobertura, kitnet, etc.)',
                    value: formatNumber(data.properties?.vertical_without_enterprise),
                    warning: true,
                    warningText: 'bla bla bla'
                  }
                ]}
              />

              <Separator />

              <ListLevel
                title = 'Total de clientes'
                value = {formatNumber(data.customers?.total)}
                subList = {[
                  {
                    label: 'Sem e-mails',
                    value: formatNumber(data.customers?.no_emails),
                  },
                  {
                    label: 'Sem telefone',
                    value: formatNumber(data.customers?.no_phones),
                  },
                  {
                    label: 'Sem corretor',
                    value: formatNumber(data.customers?.no_broker),
                  },
                ]}
              />

              <Separator />

              <ListLevel
                title = 'Total de empreendimentos'
                value = {formatNumber(data.enterprises?.total)}
                subList = {[
                  {
                    label: 'Sem endereço/CEP',
                    value: formatNumber(data.enterprises?.no_zipcode),
                  },
                  {
                    label: 'Sem foto',
                    value: formatNumber(data.enterprises?.no_photo),
                  },
                ]}
              />

              <Separator />

              <ListLevel
                title = 'Total de usuários'
                value = {formatNumber(data.brokers?.total)}
                subList = {[
                  {
                    label: 'Sem e-mail',
                    value: formatNumber(data.brokers?.no_email),
                  },
                  {
                    label: 'Sem perfil',
                    value: formatNumber(data.brokers?.no_profile),
                  },
                ]}
              />
            </Fragment>
      </div>

      <div className='approve-terms'>
        <div className='approve-terms__checkbox'>
          <CustomCheckbox
            id='approve-checkbox'
            checked={acceptTerm}
            onChange={event => setAcceptTerm(event.target.checked)}
          />
        </div>

        <div className='approve-terms__content'>
          <p className='approve-terms__text'>
            Declaro que revisei e aprovei os dados de usuários, clientes, imóveis, empreendimentos, fotos e informações adicionais dos imóveis, incluindo Quadra, Cartório (ID) e Comissão (%), quando disponíveis.
          </p>

          <p className='approve-terms__warning'>
            Estou ciente de que imóveis sem CEP não serão migrados.
          </p>

          <p className='approve-terms__text'>
            Ao aprovar esta migração, autorizo o envio desses dados para meu ambiente de produção do Kenlo Imob.
          </p>
        </div>
      </div>

      {errors && (
        <div className='approve-error'>
          <Alert alertType='error' message={messageDictionary.requestFail} />
        </div>
      )}

      <div className='approve-actions'>
        <Button
          color='dark-3'
          label='Voltar'
          onClick={() =>
            goBack()
          }
        />

        <Button
          color='neutral-1'
          primary
          label={
            !submitting ? 'APROVAR migração' : 'APROVANDO migração...'
          }
          onClick={() =>
            handleSubmit({
              resource: postChangeStatus(values),
              callbackSuccess: null,
            })
          }
          disabled={!acceptTerm || submitting}
        />
      </div>
    </PageWrapper>
  )
}

export default FormApproveMigration;
